import { CamelCasedProperties } from 'type-fest'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { TrafficImpactMapping } from 'types/api/attacks/details/create'
import { K8sObject, Strategy } from 'types/api/kubernetes/kubernetes'

export type AttackCommand =
  | 'blackhole'
  | 'cpu'
  | 'disk'
  | 'dns'
  | 'io'
  | 'latency'
  | 'memory'
  | 'packet_loss'
  | 'process_killer'
  | 'shutdown'
  | 'time_travel'
  | 'collect_certs'
export type AttackName =
  | 'CPU'
  | 'Disk'
  | 'IO'
  | 'Memory'
  | 'Process Killer'
  | 'Shutdown'
  | 'Time Travel'
  | 'Blackhole'
  | 'DNS'
  | 'Latency'
  | 'Packet Loss'
  | 'Collect Certs'

export enum AttackTypeCategory {
  Network = 'Network',
  State = 'State',
  Resource = 'Resource'
}

export type AttackCliArg = string | number
export type AttackCliArgs = [AttackCommand, ...AttackCliArg[]]

type Protocol = 'TCP' | 'UDP' | 'ICMP'
type Mode = 'r' | 'rw' | 'w'

export interface SnakeCaseAttackArgs {
  type?: AttackCommand | AttackName
  cli_args?: AttackCliArgs
  providers?: string[]
  traffic_impact_mapping?: TrafficImpactMapping
  all_cores?: boolean
  block_count?: number
  block_size?: number
  cores?: number
  corrupt?: boolean
  delay?: number
  device?: string
  directory?: string
  disable_n_t_p?: boolean
  egress_ports?: string
  exact?: boolean
  full_match?: boolean
  gigabytes?: number
  group?: string
  hostnames?: string
  ingress_ports?: string
  interval?: number
  ip_addresses?: string
  kill_children?: boolean
  length?: number
  megabytes?: number
  milliseconds?: number
  mode?: Mode
  newest?: boolean
  offset?: number
  oldest?: boolean
  percent?: number
  process?: string
  protocol?: Protocol
  reboot?: boolean
  source_ports?: string
  user?: string
  workers?: number
}

export type AttackArgs = CamelCasedProperties<SnakeCaseAttackArgs>

export interface SnakeCaseAttackCommandConfiguration {
  infraCommandType: AttackCommand
  infraCommandArgs: SnakeCaseAttackArgs
}

export interface AttackCommandConfiguration {
  infraCommandType: AttackName
  infraCommandArgs: AttackArgs
}

export enum SelectionType {
  Exact = 'EXACT',
  All = 'ALL',
  Any = 'ANY'
}

export interface ContainerSelection {
  selectionType?: SelectionType
  containerNames?: string[]
}

export interface Names {
  clusterId?: string
  kind: string
  name: string
  namespace: string
}

export interface HostTargetingStrategy {
  type: ProvenanceTargetType
  isAll?: boolean
  ids?: string[]
  multiSelectTags?: {
    [tagName: string]: string[]
  }
  containerSelection?: ContainerSelection
}

export interface ContainerTargetingStrategy {
  type: ProvenanceTargetType
  isAll?: boolean
  ids?: string[]
  multiSelectLabels?: {
    [labelName: string]: string[]
  }
  containerSelection?: ContainerSelection
}

export interface KubernetesTargetingStrategy {
  type: ProvenanceTargetType
  isAll?: boolean
  names?: Names[]
  ids?: string[]
  containerSelection?: ContainerSelection
}

export type TargetingStrategy =
  | HostTargetingStrategy
  | ContainerTargetingStrategy
  | KubernetesTargetingStrategy

export interface TargetEntry {
  serviceId?: string
  targetingStrategy: TargetingStrategy
}

export type AttackTargetingConfiguration = TargetEntry[]
export type TargetingSampleType = 'Exact' | 'Even'

export interface ExactSampling {
  type: TargetingSampleType
  count: number
}

export interface EvenSampling {
  type: TargetingSampleType
  percent?: number
  count?: number
}

export type AttackSamplingConfiguration = ExactSampling | EvenSampling

export interface SnakeCaseAttackConfiguration {
  command: SnakeCaseAttackCommandConfiguration
  targets: AttackTargetingConfiguration
  sampling: AttackSamplingConfiguration
}

export interface AttackConfiguration {
  command: AttackCommandConfiguration
  targets: AttackTargetingConfiguration
  sampling: AttackSamplingConfiguration
}

export type AttackConfigurationTargetType = 'Host' | 'Container' | 'Kubernetes'
export type CreateInfraServiceAttackResponse = Array<{
  type: AttackConfigurationTargetType
  serviceId: string
  attackId: string
}>
export type CreateKubernetesServiceAttackResponse = Array<{
  type: AttackConfigurationTargetType
  serviceId: string
  k8sAttackId: string
}>

export interface CallBack {
  url?: string
}

export interface Command {
  providers?: string[]
  trafficImpactMapping?: TrafficImpactMapping
  cliArgs?: AttackCliArgs
}

export interface InfraCommand {
  templateId?: string
  infraCommandType?: InfraCommandType
  infraCommandArgs?: InfraCommandArgs
  maxExpectedLength?: number
  length?: number
}

export enum InfraCommandType {
  CPU = 'CPU',
  IO = 'IO',
  Memory = 'Memory',
  Disk = 'Disk',
  Blackhole = 'Blackhole',
  DNS = 'DNS',
  Latency = 'Latency',
  PacketLoss = 'Packet_Loss',
  Shutdown = 'Shutdown',
  TimeTravel = 'Time_Travel',
  ProcessKiller = 'Process_Killer',
  Unknown = 'Unknown'
}

export type InfraCommandArgs = Partial<
  Omit<SnakeCaseAttackArgs, 'cli_args' | 'type'>
>

export interface InfraTarget {
  templateId?: string
  strategyType?: InfraStrategyType
  targetType?: InfraTargetType
  strategy?: Strategy
}

export enum InfraStrategyType {
  Exact = 'Exact',
  Regex = 'Regex',
  Random = 'Random'
}

export enum InfraTargetType {
  Host = 'Host',
  Container = 'Container',
  Docker = 'Docker',
  Kubernetes = 'Kubernetes'
}

export interface StrategyObject {
  k8sObjects?: Array<any>
  k8sObjectsInternal?: K8sObject[]
  count?: number
  percentage?: number
  containerSelection?: ContainerSelection
}

export interface Target {
  strategy?: StrategyObject
}

export interface TargetObject {
  strategy?: StrategyObject
}
